// Phase NAATI-MAX B2 — /en/translate/naati/category/$slug
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_CATEGORIES, getNaatiCategory, getCategoryDoctypes } from "@/data/naati-categories";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/naati/category/$slug")({
  loader: ({ params }) => {
    const cat = getNaatiCategory(params.slug);
    if (!cat) throw notFound();
    return { cat, docs: getCategoryDoctypes(cat.slug) };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const { cat, docs } = loaderData;
    const url = `${SITE}/en/translate/naati/category/${params.slug}`;
    const thUrl = `/translate/naati/category/${params.slug}`;
    const title = `${cat.nameEn} — NAATI Certified Translation | NYC Language Institute`;
    const prices = docs.map((d) => d.priceFromThb);
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/category/$slug", "en") }, { name: "description", content: ctaDesc(cat.taglineEn, "/en/translate/naati/category/$slug", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/category/$slug", "en")}, { property: "og:url", content: url },
        { property: "og:description", content: ctaDesc(cat.taglineEn, "/en/translate/naati/category/$slug", "en")},
      ],
      links: canonicalAndHreflang(thUrl, `/en/translate/naati/category/${params.slug}`),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Service",
          "@id": `${url}#service`, name: title, description: cat.taglineEn, inLanguage: "en",
          serviceType: "NAATI Certified Translation",
          provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
          areaServed: { "@type": "Country", name: "Australia" },
          offers: prices.length ? {
            "@type": "AggregateOffer", priceCurrency: "THB",
            lowPrice: Math.min(...prices), highPrice: Math.max(...prices),
            offerCount: docs.length, availability: "https://schema.org/InStock",
          } : undefined,
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "OfferCatalog",
          name: cat.nameEn, numberOfItems: docs.length,
          itemListElement: docs.map((d) => ({
            "@type": "Offer", name: d.nameEn, category: d.category,
            priceSpecification: { "@type": "UnitPriceSpecification", price: d.priceFromThb, priceCurrency: "THB", unitText: "per A4 page" },
            availability: "https://schema.org/InStock",
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#speakable`, url, name: "NAATI Certified Translation",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
        }) },
      ],
    };
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { cat, docs } = Route.useLoaderData() as { cat: NonNullable<ReturnType<typeof getNaatiCategory>>; docs: ReturnType<typeof getCategoryDoctypes> };
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
      <nav className="mb-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-amber-700">Home</Link> / <Link to="/en/translate/naati" className="hover:text-amber-700">NAATI</Link> / <span>{cat.nameEn}</span>
      </nav>
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">NAATI · Category</div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">{cat.nameEn}</h1>
      <p className="mt-3 text-lg text-slate-700" data-speakable="true">{cat.taglineEn}</p>
      <p className="mt-4 text-base leading-relaxed text-slate-700">{cat.introEn}</p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">Visas / assessments that use these documents</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {cat.visaSubclassesEn.map((v, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">{v}</li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">All documents ({docs.length})</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3">Document</th>
                <th className="px-4 py-3">Price/page</th>
                <th className="px-4 py-3">Turnaround</th>
                <th className="px-4 py-3">What to prepare</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.slug} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-navy-deep">{d.nameEn}<div className="text-xs font-normal text-slate-500">{d.nameTh}</div></td>
                  <td className="px-4 py-3">฿{d.priceFromThb.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs">{d.turnaround}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.notesEn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">Other NAATI categories</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {NAATI_CATEGORIES.filter((c) => c.slug !== cat.slug).map((c) => (
            <Link key={c.slug} to="/en/translate/naati/category/$slug" params={{ slug: c.slug }} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400">
              <div className="font-semibold text-navy-deep">{c.nameEn}</div>
              <div className="text-xs text-slate-500">{c.taglineEn}</div>
            </Link>
          ))}
        </div>
      </section>
      <QuotableStatsStrip cluster="naati" locale="en" variant="light" />
    </main>
  );
}
